/**
 * G12: persistence for blocked→known samples.
 *  - Pending push timestamps live in latency-pending.ts (the worker imports only that).
 *  - The account's `latency` setting entity (sealed like every synced setting) keeps finished
 *    samples so the numbers follow the user across devices.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { SettingEntry, settingEntityId } from '@viko/protocol'
import { useEffect, useRef } from 'react'
import { useBackend } from './backend.js'
import type { BrowserEntities } from './encrypted-entities.js'
import {
  type LatencySample,
  LatencyTracker,
  MAX_SAMPLES,
  mergeSamples,
  type Stamps,
} from './latency.js'
import { clearPending, readPending } from './latency-pending.js'
import { useInbox } from './queries.js'
import { activePoll } from './query-client.js'

type Entities = Pick<BrowserEntities, 'list' | 'write'>

// ---- synced samples ------------------------------------------------------------------------------

export async function loadLatency(entities: Entities): Promise<LatencySample[]> {
  const row = (await entities.list('setting')).find((r) => r.id === settingEntityId('latency'))
  const entry = SettingEntry.safeParse(row?.value)
  if (!entry.success || entry.data.name !== 'latency') return []
  const samples = (entry.data.value as { samples?: unknown }).samples
  return Array.isArray(samples) ? (samples as LatencySample[]).filter(validSample) : []
}

const validSample = (s: unknown): s is LatencySample => {
  const x = s as LatencySample | undefined
  return !!x && typeof x.id === 'string' && typeof x.at === 'number' && typeof x.stamps === 'object'
}

export async function saveLatency(
  entities: Entities,
  add: readonly LatencySample[],
  now = Date.now(),
): Promise<LatencySample[]> {
  const merged = mergeSamples(await loadLatency(entities), add, MAX_SAMPLES)
  await entities.write(
    'setting',
    settingEntityId('latency'),
    SettingEntry.parse({ name: 'latency', value: { samples: merged }, updatedAt: now }),
  )
  return merged
}

export function useLatencySamples() {
  const b = useBackend()
  const entities = b.entities
  return useQuery({
    queryKey: ['latency', b.artifactAccess.accountId] as const,
    queryFn: async () => (entities ? loadLatency(entities) : []),
    enabled: !!entities,
    retry: false,
    ...activePoll(5 * 60_000),
  })
}

/** Mounted once in the shell: turns resolved Inbox items into synced latency samples. */
export function useLatencyRecorder() {
  const b = useBackend()
  const qc = useQueryClient()
  const inbox = useInbox()
  const tracker = useRef(new LatencyTracker())
  const pending = useRef(new Map<string, Stamps>())
  const data = inbox.data
  // biome-ignore lint/correctness/useExhaustiveDependencies: run per inbox snapshot
  useEffect(() => {
    if (!data || inbox.isError) return
    let cancelled = false
    void (async () => {
      pending.current = await readPending().catch(() => new Map<string, Stamps>())
      if (cancelled) return
      const done = tracker.current.observe(data, Date.now(), {
        visible: typeof document === 'undefined' || document.visibilityState === 'visible',
        pending: (id) => pending.current.get(id),
      })
      if (!done.length || !b.entities) return
      try {
        const merged = await saveLatency(b.entities, done)
        await clearPending(done.map((d) => d.id))
        qc.setQueryData(['latency', b.artifactAccess.accountId], merged)
      } catch {
        // Metrics are best effort; never disturb the Inbox.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [data])
}
